/**
 * Sprint S13.1.B.1 — Step 4: mental health flags + conditional pregnancy.
 *
 * Pregnancy field rendered ONLY when sex_biological='female'. If user toggles
 * sex back к male/prefer_not_to_say after touching pregnancy, this step's
 * "Next" handler resets pregnancy_status к not_applicable. Edge case
 * documented в S13.1.B.1 brief.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { FieldMultiSelect } from "@/components/onboarding/fields/FieldMultiSelect";
import type { OnboardingData } from "@/stores/onboardingStore";


const MENTAL_HEALTH_OPTIONS = [
  "depression",
  "anxiety",
  "eating_disorder_history",
  "ptsd",
  "bipolar",
];

const PREGNANCY_VALUES: OnboardingData["pregnancy_status"][] = [
  "not_pregnant",
  "pregnant_t1",
  "pregnant_t2",
  "pregnant_t3",
  "postpartum_0_6mo",
  "postpartum_6_12mo",
];


export function Step4MentalPregnancy() {
  const { t } = useTranslation();
  const data = useOnboardingStore((s) => s.data);
  const setField = useOnboardingStore((s) => s.setField);
  const nextStep = useOnboardingStore((s) => s.nextStep);

  // Split existing flags into controlled vs other:* prefix
  const initialOther = data.mental_health_flags
    .filter((v) => v.startsWith("other:"))
    .map((v) => v.slice(6))
    .join(", ");
  const [otherFlag, setOtherFlag] = useState(initialOther);

  const showPregnancy = data.sex_biological === "female";

  const handleNext = () => {
    // Persist mental_health_flags с merged other:* entries
    const controlled = data.mental_health_flags.filter(
      (v) => !v.startsWith("other:"),
    );
    const flags = [...controlled];
    if (otherFlag.trim()) flags.push(`other:${otherFlag.trim()}`);
    setField("mental_health_flags", flags);

    // Auto-reset pregnancy_status if sex was changed away from female
    if (!showPregnancy && data.pregnancy_status !== "not_applicable") {
      setField("pregnancy_status", "not_applicable");
    }

    nextStep();
  };

  const mentalOptions = MENTAL_HEALTH_OPTIONS.map((v) => ({
    value: v,
    label: t(`onboarding.step.mental_pregnancy.flag.${v}`),
  }));

  const controlledFlags = data.mental_health_flags.filter(
    (v) => !v.startsWith("other:"),
  );

  // Default selection для pregnancy field if entering as female fresh
  const pregnancySelection =
    data.pregnancy_status === "not_applicable"
      ? "not_pregnant"
      : data.pregnancy_status;

  return (
    <div className="space-y-6">
      <div>
        <h2
          id="step-heading"
          tabIndex={-1}
          className="mb-2 text-xl font-semibold text-navy-deep sm:text-2xl"
        >
          {t("onboarding.step.mental_pregnancy.title")}
        </h2>
        <p className="text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.step.mental_pregnancy.subtitle")}
        </p>
        <p className="mt-1 text-[12px] text-taupe-light">
          {t("onboarding.step.mental_pregnancy.skip_hint")}
        </p>
      </div>

      <FieldMultiSelect
        label={t("onboarding.step.mental_pregnancy.flags_label")}
        hint={t("onboarding.step.mental_pregnancy.flags_hint")}
        options={mentalOptions}
        selected={controlledFlags}
        onChange={(values) => setField("mental_health_flags", values)}
        allowOther
        otherValue={otherFlag}
        onOtherChange={setOtherFlag}
        otherPlaceholder={t("onboarding.step.mental_pregnancy.other_placeholder")}
      />

      {showPregnancy ? (
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium text-navy">
            {t("onboarding.step.mental_pregnancy.pregnancy_label")}
          </span>
          <span className="mb-2 block text-[12px] text-taupe">
            {t("onboarding.step.mental_pregnancy.pregnancy_hint")}
          </span>
          <select
            value={pregnancySelection}
            onChange={(e) =>
              setField(
                "pregnancy_status",
                e.target.value as OnboardingData["pregnancy_status"],
              )
            }
            className="w-full rounded-lg border border-cream-border bg-cream-light px-4 py-3 text-[15px] text-navy-deep focus:border-amber/40 focus:outline-none focus:ring-2 focus:ring-amber/60"
          >
            {PREGNANCY_VALUES.map((v) => (
              <option key={v} value={v}>
                {t(`onboarding.step.mental_pregnancy.pregnancy.${v}`)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div className="pt-2">
        <Button
          type="button"
          onClick={handleNext}
          data-onboarding-next
          className="w-full sm:w-auto"
        >
          {t("onboarding.nav.next")}
        </Button>
      </div>
    </div>
  );
}
